import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  CloudArrowUpIcon,
  DocumentArrowDownIcon,
  UserPlusIcon,
  UsersIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  AcademicCapIcon,
  UserIcon,
  UserGroupIcon,
  ArrowDownTrayIcon,
  ArrowRightIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { PhoneNumberInput } from '@/components/ui/PhoneNumberInput'
import { useAuth } from '@/hooks/useAuth'
import { useUserRole } from '@/hooks/useUserRole'
import { createUser, createFamily, enrolStudent, getUsers, type CreateUserRequest, type User } from '@/api/usersApi'
import { getRoles } from '@/api/rolesApi'
import { getGrades, getStreams, type Grade, type Stream } from '@/api/academicApi'
import toast from 'react-hot-toast'

interface BulkUploadResult {
  success: number
  failed: number
  total: number
  errors: Array<{
    row: number
    email: string
    error: string
  }>
}

interface CSVRow {
  // Student fields
  studentFirstName?: string
  studentLastName?: string
  studentEmail?: string
  studentPhone?: string
  studentGrade?: string
  studentStream?: string
  studentDateOfBirth?: string
  studentId?: string
  // Parent fields  
  parentFirstName?: string
  parentLastName?: string
  parentEmail?: string
  parentPhone?: string
  parentOccupation?: string
  relationshipToStudent?: string
  parentAddress?: string
  emergencyContact?: string
  emergencyPhone?: string
  // Legacy individual fields (for backwards compatibility)
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  grade?: string
  dateOfBirth?: string
  occupation?: string
  relationship?: string
  address?: string
  // Internal field for linking
  _createdStudentId?: string
}

// Form schemas for two-step wizard
const studentFormSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Valid email address is required'),
  phone: z.string().optional(),
  gradeId: z.string().min(1, 'Grade is required'),
  streamId: z.string().min(1, 'Stream is required'),
  dateOfBirth: z.string().min(1, 'Date of birth is required'),
  studentId: z.string().optional(),
})

const parentFormSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Valid email address is required'),
  phone: z.string().min(10, 'Valid phone number is required'),
  occupation: z.string().optional(),
  relationship: z.string().optional(),
  address: z.string().optional(),
  emergencyContact: z.string().optional(),
  emergencyPhone: z.string().optional(),
})

type StudentFormData = z.infer<typeof studentFormSchema>
type ParentFormData = z.infer<typeof parentFormSchema>

// Enrolled family type for display
interface EnrolledFamily {
  id: string
  student: {
    id: string
    fullName: string
    email: string
    studentId?: string
    phone?: string
    createdAt: string
  }
  grade?: string
  stream?: string
  parents: Array<{
    id: string
    fullName: string
    email: string
    phone?: string
    relationship?: string
  }>
}

const OnboardPage: React.FC = () => {
  const { user } = useAuth()
  const { userRole } = useUserRole()
  const [uploadType, setUploadType] = useState<'students' | 'parents' | 'families'>('families')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState<BulkUploadResult | null>(null)
  const [csvData, setCsvData] = useState<CSVRow[]>([])
  const [showPreview, setShowPreview] = useState(false)
  const [availableRoles, setAvailableRoles] = useState<{ id: string, name: string, type: string }[]>([])
  const [grades, setGrades] = useState<Grade[]>([])
  const [streams, setStreams] = useState<Stream[]>([])
  const [selectedGrade, setSelectedGrade] = useState<string>('')
  const [gradeSearchQuery, setGradeSearchQuery] = useState('')
  const [streamSearchQuery, setStreamSearchQuery] = useState('')
  const [gradeSearchResults, setGradeSearchResults] = useState<Grade[]>([])
  const [streamSearchResults, setStreamSearchResults] = useState<Stream[]>([])
  const [gradeSearchLoading, setGradeSearchLoading] = useState(false)
  const [streamSearchLoading, setStreamSearchLoading] = useState(false)
  const [showGradeDropdown, setShowGradeDropdown] = useState(false)
  const [showStreamDropdown, setShowStreamDropdown] = useState(false)

  // Enrolled families state
  const [enrolledFamilies, setEnrolledFamilies] = useState<EnrolledFamily[]>([])
  const [loadingFamilies, setLoadingFamilies] = useState(false)
  const [familiesPage, setFamiliesPage] = useState(1)
  const [totalFamilies, setTotalFamilies] = useState(0)
  const [totalParents, setTotalParents] = useState(0)
  const familiesPerPage = 10

  // Form wizard state
  const [showFormWizard, setShowFormWizard] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [studentData, setStudentData] = useState<StudentFormData | null>(null)

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  // Form instances
  const studentForm = useForm<StudentFormData>({
    resolver: zodResolver(studentFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      gradeId: '',
      streamId: '',
      dateOfBirth: '',
      studentId: '',
    }
  })

  const parentForm = useForm<ParentFormData>({
    resolver: zodResolver(parentFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      occupation: '',
      relationship: '',
      address: '',
      emergencyContact: '',
      emergencyPhone: '',
    }
  })

  useEffect(() => {
    fetchRoles()
    fetchGrades()
  }, [])

  // Fetch enrolled families after roles are loaded
  useEffect(() => {
    if (availableRoles.length > 0) {
      fetchEnrolledFamilies()
    }
  }, [availableRoles, familiesPage])

  useEffect(() => {
    if (selectedGrade) {
      fetchStreamsByGrade(selectedGrade)
    } else {
      setStreams([])
      setStreamSearchResults([])
    }
  }, [selectedGrade])

  // Debounced search for grades
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (gradeSearchQuery.trim()) {
        searchGrades(gradeSearchQuery.trim())
      } else {
        // Show all grades when no search query
        setGradeSearchResults(grades)
      }
    }, 300) // 300ms debounce

    return () => clearTimeout(timeoutId)
  }, [gradeSearchQuery, grades])

  // Debounced search for streams
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (streamSearchQuery.trim() && selectedGrade) {
        searchStreams(streamSearchQuery.trim(), selectedGrade)
      } else if (selectedGrade) {
        // Show all streams when no search query but grade is selected
        setStreamSearchResults(streams)
      } else {
        setStreamSearchResults([])
      }
    }, 300) // 300ms debounce

    return () => clearTimeout(timeoutId)
  }, [streamSearchQuery, selectedGrade, streams])

  const fetchRoles = async () => {
    try {
      const response = await getRoles()
      const rolesWithType = response.data.map(role => ({
        id: role.id,
        name: role.name,
        type: role.name.toLowerCase().includes('student') ? 'student' :
              role.name.toLowerCase().includes('parent') ? 'parent' :
              role.name.toLowerCase().includes('teacher') ? 'teacher' :
              'other'
      }))
      setAvailableRoles(rolesWithType)
    } catch (error) {
      console.error('Failed to fetch roles:', error)
    }
  }

  const fetchGrades = async () => {
    try {
      const response = await getGrades()
      const gradesData = response.grades || []
      setGrades(Array.isArray(gradesData) ? gradesData : [])
      console.log('Loaded grades:', gradesData.length, 'grades')
    } catch (error) {
      console.error('Failed to fetch grades:', error)
      setGrades([])
      toast.error('Failed to load grades')
    }
  }

  const fetchStreamsByGrade = async (gradeId: string) => {
    try {
      const streamsData = await getStreams({ gradeId })
      setStreams(Array.isArray(streamsData) ? streamsData : [])
      console.log('Loaded streams for grade:', gradeId, '→', streamsData.length, 'streams')
    } catch (error) {
      console.error('Failed to fetch streams:', error)
      setStreams([])
      toast.error('Failed to load streams for selected grade')
    }
  }

  const fetchEnrolledFamilies = async () => {
    setLoadingFamilies(true)
    try {
      // Get the student and parent role IDs
      const studentRoleId = availableRoles.find(role => role.type === 'student')?.id
      const parentRoleId = availableRoles.find(role => role.type === 'parent')?.id

      // Fetch students sorted by most recent first
      const response = await getUsers({
        roleId: studentRoleId,
        page: familiesPage,
        limit: familiesPerPage,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      })

      // Also fetch parent count
      if (parentRoleId) {
        try {
          const parentResponse = await getUsers({
            roleId: parentRoleId,
            page: 1,
            limit: 1
          })
          setTotalParents(parentResponse.pagination?.totalUsers || 0)
        } catch {
          setTotalParents(0)
        }
      }

      // Transform users into enrolled families format
      const families: EnrolledFamily[] = response.users.map((student: User) => ({
        id: student.id,
        student: {
          id: student.id,
          fullName: student.fullName || `${student.firstName} ${student.lastName}`,
          email: student.email,
          studentId: student.studentId,
          phone: student.phone,
          createdAt: student.createdAt
        },
        grade: student.grade?.name || student.academicSummary?.gradeName,
        stream: student.stream?.name || student.academicSummary?.streamName,
        parents: student.parents || []
      }))

      setEnrolledFamilies(families)
      setTotalFamilies(response.pagination?.totalUsers || 0)
    } catch (error) {
      console.error('Failed to fetch enrolled families:', error)
      setEnrolledFamilies([])
    } finally {
      setLoadingFamilies(false)
    }
  }

  const searchGrades = async (searchQuery: string) => {
    setGradeSearchLoading(true)
    try {
      const response = await getGrades({ 
        search: searchQuery,
        limit: 50, // Get more results for search
        status: 'active'
      })
      const gradesData = response.grades || []
      setGradeSearchResults(Array.isArray(gradesData) ? gradesData : [])
      console.log('Grade search results for "' + searchQuery + '":', gradesData.length, 'grades')
    } catch (error) {
      console.error('Failed to search grades:', error)
      setGradeSearchResults([])
    } finally {
      setGradeSearchLoading(false)
    }
  }

  const searchStreams = async (searchQuery: string, gradeId: string) => {
    setStreamSearchLoading(true)
    try {
      // First get all streams for the grade, then we can search through them
      const streamsData = await getStreams({ gradeId })
      const allStreams = Array.isArray(streamsData) ? streamsData : []
      
      // Filter streams by search query (since backend might not support stream search yet)
      const filteredStreams = allStreams.filter(stream =>
        stream.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (stream.classTeacher && stream.classTeacher.toLowerCase().includes(searchQuery.toLowerCase()))
      )
      
      setStreamSearchResults(filteredStreams)
      console.log('Stream search results for "' + searchQuery + '" in grade:', filteredStreams.length, 'streams')
    } catch (error) {
      console.error('Failed to search streams:', error)
      setStreamSearchResults([])
    } finally {
      setStreamSearchLoading(false)
    }
  }



  const parseCSV = (csvText: string): CSVRow[] => {
    const lines = csvText.split('\n').filter(line => line.trim())
    if (lines.length < 2) return []

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
    const rows = lines.slice(1).map((line, index) => {
      const values = line.split(',').map(v => v.trim())
      const row: any = {}
      
      headers.forEach((header, i) => {
        const value = values[i] || ''
        
        // Map CSV headers to our expected fields - support both individual and family formats
        switch (header) {
          // Student fields
          case 'studentfirstname':
          case 'student_first_name':
          case 'firstname':
          case 'first_name':
          case 'first name':
            row.studentFirstName = value || row.firstName || value
            row.firstName = value // Legacy support
            break
          case 'studentlastname':
          case 'student_last_name':
          case 'lastname':
          case 'last_name':
          case 'last name':
            row.studentLastName = value || row.lastName || value
            row.lastName = value // Legacy support
            break
          case 'studentemail':
          case 'student_email':
          case 'email':
          case 'email_address':
            row.studentEmail = value || row.email || value
            row.email = value // Legacy support
            break
          case 'studentphone':
          case 'student_phone':
          case 'phone':
          case 'phone_number':
            row.studentPhone = value || row.phone || value
            row.phone = value // Legacy support
            break
          case 'studentgrade':
          case 'student_grade':
          case 'grade':
          case 'class':
          case 'level':
            row.studentGrade = value
            row.grade = value // Legacy support
            break
          case 'studentdateofbirth':
          case 'student_date_of_birth':
          case 'date_of_birth':
          case 'dob':
          case 'dateofbirth':
            row.studentDateOfBirth = value
            row.dateOfBirth = value // Legacy support
            break
          case 'studentid':
          case 'student_id':
            row.studentId = value
            break
          case 'studentstream':
          case 'student_stream':
          case 'stream':
          case 'class_stream':
            row.studentStream = value
            break
          // Parent fields
          case 'parentfirstname':
          case 'parent_first_name':
            row.parentFirstName = value
            break
          case 'parentlastname':
          case 'parent_last_name':
            row.parentLastName = value
            break
          case 'parentemail':
          case 'parent_email':
            row.parentEmail = value
            break
          case 'parentphone':
          case 'parent_phone':
            row.parentPhone = value
            break
          case 'parentoccupation':
          case 'parent_occupation':
          case 'occupation':
          case 'job':
            row.parentOccupation = value
            row.occupation = value // Legacy support
            break
          case 'relationshiptostudent':
          case 'relationship_to_student':
          case 'relationship':
          case 'relation':
            row.relationshipToStudent = value
            row.relationship = value // Legacy support
            break
          case 'parentaddress':
          case 'parent_address':
          case 'address':
          case 'home_address':
            row.parentAddress = value
            row.address = value // Legacy support
            break
          case 'emergencycontact':
          case 'emergency_contact':
            row.emergencyContact = value
            break
          case 'emergencyphone':
          case 'emergency_phone':
            row.emergencyPhone = value
            break
          default:
            row[header] = value
        }
      })
      
      return row
    })

    return rows.filter(row => 
      // Valid if has student data OR parent data (or both)
      (row.studentFirstName && row.studentLastName && row.studentEmail) ||
      (row.parentFirstName && row.parentLastName && row.parentEmail) ||
      (row.firstName && row.lastName && row.email) // Legacy format
    )
  }

  const handleFileSelect = (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file')
      return
    }
    setSelectedFile(file)
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const processSelectedFile = () => {
    if (!selectedFile) return

    const reader = new FileReader()
    reader.onload = (e) => {
      const csvText = e.target?.result as string
      const parsed = parseCSV(csvText)

      if (parsed.length === 0) {
        toast.error('No valid data found in CSV file')
        return
      }

      setCsvData(parsed)
      setShowPreview(true)
      setShowUploadModal(false)
      setSelectedFile(null)
      toast.success(`Parsed ${parsed.length} records from CSV`)
    }

    reader.readAsText(selectedFile)
  }

  const closeUploadModal = () => {
    setShowUploadModal(false)
    setSelectedFile(null)
    setIsDragging(false)
  }

  const handleBulkUpload = async () => {
    if (csvData.length === 0) {
      toast.error('No data to upload')
      return
    }

    setIsUploading(true)
    setUploadProgress(0)
    
    // Get role IDs for students and parents
    const studentRoleId = availableRoles.find(role => role.type === 'student')?.id
    const parentRoleId = availableRoles.find(role => role.type === 'parent')?.id
    
    if (!studentRoleId || !parentRoleId) {
      toast.error('Student or Parent roles not found')
      setIsUploading(false)
      return
    }

    const results: BulkUploadResult = {
      success: 0,
      failed: 0,
      total: csvData.length * 2, // Each row creates 2 users (student + parent)
      errors: []
    }

    for (let i = 0; i < csvData.length; i++) {
      const row = csvData[i]
      
      try {
        // Create Student Account
        if (row.studentFirstName && row.studentLastName && row.studentEmail) {
          // Find grade and stream IDs from names
          const gradeName = row.studentGrade || row.grade || '';
          const streamName = row.studentStream || '';
          
          let gradeId = '';
          let streamId = '';
          
          if (gradeName) {
            const grade = grades.find(g => g.name.toLowerCase() === gradeName.toLowerCase());
            gradeId = grade ? (grade.id) : '';
          }
          
          if (streamName && gradeId) {
            // Find stream in the current streams or load streams for the grade
            let availableStreams = streams;
            if (gradeId !== selectedGrade) {
              // Need to fetch streams for this grade
              try {
                availableStreams = await getStreams({ gradeId });
              } catch (error) {
                console.warn('Could not fetch streams for grade:', gradeId);
              }
            }
            const stream = availableStreams.find(s => s.name.toLowerCase() === streamName.toLowerCase());
            streamId = stream ? (stream.id) : '';
          }

          const studentData: CreateUserRequest = {
            firstName: row.studentFirstName || row.firstName || '',
            lastName: row.studentLastName || row.lastName || '',
            email: row.studentEmail || row.email || '',
            roleId: studentRoleId,
            phone: row.studentPhone || row.phone,
            institutionId: user?.institutionId,
            gradeId: gradeId || undefined,
            streamId: streamId || undefined,
            studentId: row.studentId || undefined
          }

          const studentResponse = await createUser(studentData)
          const createdStudentId = studentResponse.data.user.id
          results.success++

          // Store student ID for parent linking
          row._createdStudentId = createdStudentId
        }

        // Create Parent Account (if parent data provided)
        if (row.parentFirstName && row.parentLastName && row.parentEmail) {
          const parentData: CreateUserRequest = {
            firstName: row.parentFirstName,
            lastName: row.parentLastName,
            email: row.parentEmail,
            roleId: parentRoleId,
            phone: row.parentPhone,
            institutionId: user?.institutionId,
            childrenIds: row._createdStudentId ? [row._createdStudentId] : [] // Link to student if created
          }

          await createUser(parentData)
          results.success++
        } else if (!row.parentFirstName && uploadType === 'families') {
          // If no parent data in families upload, still count as partial success
          results.failed++
          results.errors.push({
            row: i + 1,
            email: row.parentEmail || 'N/A',
            error: 'No parent data provided in family row'
          })
        }
        
      } catch (error: any) {
        results.failed++
        results.errors.push({
          row: i + 1,
          email: row.studentEmail || row.parentEmail || row.email || 'Unknown',
          error: error?.response?.data?.message || 'Unknown error'
        })
      }

      // Update progress
      const progress = ((i + 1) / csvData.length) * 100
      setUploadProgress(progress)
      
      // Small delay to prevent overwhelming the API
      await new Promise(resolve => setTimeout(resolve, 150))
    }

    setUploadResult(results)
    setIsUploading(false)

    if (results.success > 0) {
      toast.success(`Successfully created ${results.success} accounts from family data`)
      // Refresh the enrolled families list
      setFamiliesPage(1)
      fetchEnrolledFamilies()
    }

    if (results.failed > 0) {
      toast.error(`Failed to create ${results.failed} accounts`)
    }
  }

  const downloadTemplate = (type: 'students' | 'parents' | 'families') => {
    let headers, sampleData, filename

    if (type === 'families') {
      // Combined family template with both student and parent in one row
      headers = [
        'studentFirstName', 'studentLastName', 'studentEmail', 'studentPhone', 'studentGrade', 'studentStream', 'studentDateOfBirth', 'studentId',
        'parentFirstName', 'parentLastName', 'parentEmail', 'parentPhone', 'parentOccupation', 'relationshipToStudent', 'parentAddress', 'emergencyContact', 'emergencyPhone'
      ]
      sampleData = [
        'John,Doe,john.doe@students.school.edu,+254712345678,Grade 5,Stream A,2010-05-15,STU24001,Mary,Doe,mary.doe@parent.co.ke,+254734567890,Engineer,Mother,123 Oak Street Nairobi,James Doe,+254700345678',
        'Jane,Smith,jane.smith@students.school.edu,+254723456789,Grade 6,Stream B,2009-08-22,STU24002,Peter,Smith,peter.smith@parent.co.ke,+254745678901,Teacher,Father,456 Pine Avenue Nairobi,Grace Smith,+254700456789'
      ]
      filename = 'families_template.csv'
    } else if (type === 'students') {
      headers = ['firstName', 'lastName', 'email', 'phone', 'grade', 'stream', 'dateOfBirth', 'studentId', 'parentEmail', 'parentPhone']
      sampleData = [
        'John,Doe,john.doe@students.school.edu,+254712345678,Grade 5,Stream A,2010-05-15,STU24001,john.parent@example.com,+254700123456',
        'Jane,Smith,jane.smith@students.school.edu,+254723456789,Grade 6,Stream B,2009-08-22,STU24002,jane.parent@example.com,+254700234567'
      ]
      filename = 'students_template.csv'
    } else {
      headers = ['firstName', 'lastName', 'email', 'phone', 'occupation', 'relationship', 'address', 'emergencyContact', 'emergencyPhone']
      sampleData = [
        'Mary,Johnson,mary.johnson@parent.co.ke,+254734567890,Engineer,Mother,123 Oak Street Nairobi,James Johnson,+254700345678',
        'Peter,Kamau,peter.kamau@parent.co.ke,+254745678901,Teacher,Father,456 Pine Avenue Nairobi,Grace Kamau,+254700456789'
      ]
      filename = 'parents_template.csv'
    }

    const csvContent = [headers.join(','), ...sampleData].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    window.URL.revokeObjectURL(url)
    
    toast.success(`${type} template downloaded`)
  }

  const resetUpload = () => {
    setCsvData([])
    setShowPreview(false)
    setUploadResult(null)
    setUploadProgress(0)
  }

  const handleStudentFormNext = (data: StudentFormData) => {
    setStudentData(data)
    setCurrentStep(2)
  }

  const handleFormWizardSubmit = async (parentData: ParentFormData) => {
    if (!studentData) {
      toast.error('Student data is missing')
      return
    }

    setIsUploading(true)
    
    try {
      // Use the family creation endpoint
      const familyPayload = {
        student: {
          firstName: studentData.firstName,
          lastName: studentData.lastName,
          email: studentData.email,
          phone: studentData.phone,
          gradeId: studentData.gradeId,
          streamId: studentData.streamId,
          studentId: studentData.studentId || undefined
        },
        parent: {
          firstName: parentData.firstName,
          lastName: parentData.lastName,
          email: parentData.email,
          phone: parentData.phone,
          occupation: parentData.occupation,
          relationship: parentData.relationship,
          address: parentData.address,
          emergencyContact: parentData.emergencyContact,
          emergencyPhone: parentData.emergencyPhone
        },
        institutionId: user?.role === 'SUPER_ADMIN' ? user?.institutionId : undefined
      }

      const response = await enrolStudent(familyPayload)

      toast.success(`Successfully created family accounts! Student: ${response.data.student.studentId}, Parent linked automatically.`, {
        duration: 6000
      })

      // Reset wizard
      setShowFormWizard(false)
      setCurrentStep(1)
      setStudentData(null)
      studentForm.reset()
      parentForm.reset()

      // Refresh the enrolled families list
      setFamiliesPage(1)
      fetchEnrolledFamilies()
      
    } catch (error: any) {
      console.error('Failed to create family accounts:', error)
      toast.error(error?.response?.data?.message || 'Failed to create family accounts')
    } finally {
      setIsUploading(false)
    }
  }

  const resetWizard = () => {
    setShowFormWizard(false)
    setCurrentStep(1)
    setStudentData(null)
    setSelectedGrade('')
    setGradeSearchQuery('')
    setStreamSearchQuery('')
    setStreams([])
    setGradeSearchResults([])
    setStreamSearchResults([])
    setShowGradeDropdown(false)
    setShowStreamDropdown(false)
    studentForm.reset()
    parentForm.reset()
  }

  const handleGradeSelection = (grade: Grade) => {
    const gradeId = grade.id
    setSelectedGrade(gradeId)
    studentForm.setValue('gradeId', gradeId)
    studentForm.setValue('streamId', '') // Reset stream when grade changes
    setGradeSearchQuery(grade.name)
    setStreamSearchQuery('') // Reset stream search
    setShowGradeDropdown(false) // Hide dropdown
    setShowStreamDropdown(false) // Hide stream dropdown
    setGradeSearchResults([]) // Clear search results
    setStreamSearchResults([]) // Clear stream search results
  }

  const handleStreamSelection = (stream: Stream) => {
    const streamId = stream.id
    studentForm.setValue('streamId', streamId)
    setStreamSearchQuery(stream.name)
    setShowStreamDropdown(false) // Hide dropdown
    setStreamSearchResults([]) // Clear search results
  }

  // Use search results when available, otherwise show all data
  const displayGrades = gradeSearchQuery ? gradeSearchResults : grades
  const displayStreams = streamSearchQuery ? streamSearchResults : streams

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Students</p>
              <p className="text-2xl font-bold text-gray-900">{totalFamilies}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <AcademicCapIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Parents</p>
              <p className="text-2xl font-bold text-gray-900">{totalParents}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <UserGroupIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Add Student</p>
              <button
                onClick={() => setShowFormWizard(true)}
                className="mt-1 text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
              >
                <UserPlusIcon className="h-4 w-4 mr-1" />
                Enroll Now
              </button>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <UserPlusIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Bulk Upload</p>
              <button
                onClick={() => setShowUploadModal(true)}
                className="mt-1 text-sm text-purple-600 hover:text-purple-800 font-medium flex items-center"
              >
                <CloudArrowUpIcon className="h-4 w-4 mr-1" />
                Upload CSV
              </button>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <CloudArrowUpIcon className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Bar */}
      <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowFormWizard(true)}
            className="btn btn-primary"
          >
            <UserPlusIcon className="h-4 w-4 mr-2" />
            Add Student & Parent
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="btn btn-secondary"
          >
            <CloudArrowUpIcon className="h-4 w-4 mr-2" />
            Upload CSV
          </button>
        </div>
        <button
          onClick={() => downloadTemplate('families')}
          className="text-sm text-gray-600 hover:text-gray-900 flex items-center"
        >
          <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
          Download Template
        </button>
      </div>

      {/* CSV Preview */}
      <AnimatePresence>
        {showPreview && csvData.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="dashboard-card"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Data Preview ({csvData.length} records)
              </h3>
              <div className="flex space-x-2">
                <button
                  onClick={resetUpload}
                  className="btn btn-secondary"
                >
                  Clear
                </button>
                <button
                  onClick={handleBulkUpload}
                  disabled={isUploading}
                  className="btn btn-primary"
                >
                  {isUploading ? (
                    <>
                      <ArrowDownTrayIcon className="h-4 w-4 mr-2 animate-bounce" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                      Start Upload
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Progress Bar */}
            {isUploading && (
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span>Upload Progress</span>
                  <span>{Math.round(uploadProgress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Student Email</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Parent</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Parent Email</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Relationship</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {csvData.slice(0, 10).map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm">
                        {(row.studentFirstName || row.firstName) && (row.studentLastName || row.lastName) 
                          ? `${row.studentFirstName || row.firstName} ${row.studentLastName || row.lastName}`
                          : '-'
                        }
                      </td>
                      <td className="px-3 py-2 text-sm">{row.studentEmail || row.email || '-'}</td>
                      <td className="px-3 py-2 text-sm">
                        {row.studentGrade || row.grade || '-'}
                        {row.studentStream && (
                          <div className="text-xs text-gray-500">Stream: {row.studentStream}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm">
                        {row.parentFirstName && row.parentLastName 
                          ? `${row.parentFirstName} ${row.parentLastName}`
                          : '-'
                        }
                      </td>
                      <td className="px-3 py-2 text-sm">{row.parentEmail || '-'}</td>
                      <td className="px-3 py-2 text-sm">{row.relationshipToStudent || row.relationship || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {csvData.length > 10 && (
                <div className="px-3 py-2 text-xs text-gray-500 text-center bg-gray-50">
                  Showing first 10 records. {csvData.length - 10} more records will be uploaded.
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload Results */}
      <AnimatePresence>
        {uploadResult && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="dashboard-card"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Upload Results</h3>
              <button
                onClick={resetUpload}
                className="btn btn-secondary"
              >
                Start New Upload
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                  <span className="text-sm font-medium text-green-900">Successful</span>
                </div>
                <p className="text-2xl font-bold text-green-900 mt-1">{uploadResult.success}</p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <XCircleIcon className="h-5 w-5 text-red-600 mr-2" />
                  <span className="text-sm font-medium text-red-900">Failed</span>
                </div>
                <p className="text-2xl font-bold text-red-900 mt-1">{uploadResult.failed}</p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <UsersIcon className="h-5 w-5 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-blue-900">Total</span>
                </div>
                <p className="text-2xl font-bold text-blue-900 mt-1">{uploadResult.total}</p>
              </div>
            </div>

            {/* Error Details */}
            {uploadResult.errors.length > 0 && (
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-3">Upload Errors</h4>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-60 overflow-y-auto">
                  <div className="space-y-2">
                    {uploadResult.errors.map((error, index) => (
                      <div key={index} className="flex items-start space-x-2">
                        <ExclamationTriangleIcon className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm">
                          <span className="font-medium text-red-900">Row {error.row}:</span>
                          <span className="text-red-700 ml-1">{error.email}</span>
                          <p className="text-red-600 text-xs mt-1">{error.error}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Enrolled Families List */}
      <div className="dashboard-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <UsersIcon className="h-5 w-5 text-indigo-600 mr-2" />
            Recently Enrolled Families
          </h3>
          <div className="text-sm text-gray-500">
            {totalFamilies > 0 && (
              <span>Showing {((familiesPage - 1) * familiesPerPage) + 1}-{Math.min(familiesPage * familiesPerPage, totalFamilies)} of {totalFamilies}</span>
            )}
          </div>
        </div>

        {loadingFamilies ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="ml-3 text-gray-500">Loading enrolled families...</span>
          </div>
        ) : enrolledFamilies.length === 0 ? (
          <div className="text-center py-12">
            <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h4 className="mt-4 text-lg font-medium text-gray-900">No families enrolled yet</h4>
            <p className="mt-2 text-gray-500">
              Use the "Add Student & Parent" button above to enroll your first family.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade / Stream</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parent(s)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enrolled</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {enrolledFamilies.map((family) => (
                    <tr key={family.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{family.student.fullName}</div>
                            <div className="text-sm text-gray-500">{family.student.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {family.student.studentId || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{family.grade || 'Not assigned'}</div>
                        {family.stream && (
                          <div className="text-sm text-gray-500">{family.stream}</div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {family.parents.length > 0 ? (
                          <div className="space-y-1">
                            {family.parents.map((parent, idx) => (
                              <div key={parent.id || idx} className="flex items-center">
                                <div className="flex-shrink-0 h-6 w-6 bg-purple-100 rounded-full flex items-center justify-center mr-2">
                                  <UserGroupIcon className="h-3 w-3 text-purple-600" />
                                </div>
                                <div>
                                  <span className="text-sm text-gray-900">{parent.fullName}</span>
                                  {parent.relationship && (
                                    <span className="text-xs text-gray-500 ml-1">({parent.relationship})</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400 italic">No parent linked</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(family.student.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalFamilies > familiesPerPage && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <button
                  onClick={() => setFamiliesPage(prev => Math.max(1, prev - 1))}
                  disabled={familiesPage === 1}
                  className="btn btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-1" />
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {familiesPage} of {Math.ceil(totalFamilies / familiesPerPage)}
                </span>
                <button
                  onClick={() => setFamiliesPage(prev => prev + 1)}
                  disabled={familiesPage >= Math.ceil(totalFamilies / familiesPerPage)}
                  className="btn btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ArrowRightIcon className="h-4 w-4 ml-1" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* CSV Upload Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={closeUploadModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Upload CSV File</h3>
                    <p className="text-sm text-gray-500">Import students and parents from CSV</p>
                  </div>
                  <button
                    onClick={closeUploadModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                {/* Drag & Drop Area */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDragging
                      ? 'border-blue-500 bg-blue-50'
                      : selectedFile
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {selectedFile ? (
                    <div className="space-y-3">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                        <CheckCircleIcon className="h-8 w-8 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                        <p className="text-xs text-gray-500">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <button
                        onClick={() => setSelectedFile(null)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                        <CloudArrowUpIcon className={`h-8 w-8 ${isDragging ? 'text-blue-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {isDragging ? 'Drop your file here' : 'Drag and drop your CSV file'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">or</p>
                      </div>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".csv"
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                        <span className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                          Browse Files
                        </span>
                      </label>
                    </div>
                  )}
                </div>

                {/* File Requirements */}
                <div className="mt-4 text-xs text-gray-500 space-y-1">
                  <p>• CSV file with headers required</p>
                  <p>• Maximum 500 records per upload</p>
                  <p>• <button onClick={() => downloadTemplate('families')} className="text-blue-600 hover:underline">Download template</button> for correct format</p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <Button
                  type="button"
                  onClick={closeUploadModal}
                  variant="secondary"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={processSelectedFile}
                  disabled={!selectedFile}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  Process File
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Two-Step Form Wizard Modal */}
      <AnimatePresence>
        {showFormWizard && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => resetWizard()}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Wizard Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Add Family - Step {currentStep} of 2
                    </h3>
                    <p className="text-sm text-gray-500">
                      {currentStep === 1 ? 'Enter student details' : 'Enter parent details'}
                    </p>
                  </div>
                  <button
                    onClick={resetWizard}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
                
                {/* Progress Indicator */}
                <div className="flex items-center mt-4">
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                    }`}>
                      1
                    </div>
                    <span className="ml-2 text-sm font-medium text-gray-900">Student Details</span>
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="h-1 bg-gray-300 rounded-full">
                      <div className={`h-1 rounded-full transition-all ${
                        currentStep >= 2 ? 'bg-blue-600 w-full' : 'bg-gray-300 w-0'
                      }`}></div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                    }`}>
                      2
                    </div>
                    <span className="ml-2 text-sm font-medium text-gray-900">Parent Details</span>
                  </div>
                </div>
              </div>

              {/* Step 1: Student Details */}
              {currentStep === 1 && (
                <form onSubmit={studentForm.handleSubmit(handleStudentFormNext)} className="p-6 space-y-4" autoComplete="off">
                  <div className="text-center mb-6">
                    <UserIcon className="mx-auto h-12 w-12 text-blue-600" />
                    <h4 className="mt-4 text-lg font-semibold text-gray-900">Student Information</h4>
                    <p className="text-sm text-gray-600">Enter the student's basic details</p>
                  </div>

                  {/* Hidden fields to prevent autofill */}
                  <input type="email" style={{display:'none'}} autoComplete="off" />
                  <input type="text" style={{display:'none'}} autoComplete="off" />
                  <input type="password" style={{display:'none'}} autoComplete="off" />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      {...studentForm.register('firstName')}
                      label="Student First Name"
                      placeholder="e.g., John"
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      error={studentForm.formState.errors.firstName?.message}
                    />

                    <Input
                      {...studentForm.register('lastName')}
                      label="Student Last Name"
                      placeholder="e.g., Doe"
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      error={studentForm.formState.errors.lastName?.message}
                    />

                    <Input
                      {...studentForm.register('email')}
                      type="email"
                      label="Student Email"
                      placeholder="e.g., john.doe@students.school.edu"
                      autoComplete="new-email"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      error={studentForm.formState.errors.email?.message}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Student Phone (Optional)
                      </label>
                      <PhoneNumberInput
                        value={studentForm.watch('phone') || ''}
                        onChange={(phone) => studentForm.setValue('phone', phone)}
                        placeholder="Enter phone number"
                        error={studentForm.formState.errors.phone?.message}
                      />
                    </div>

                    {/* Grade Selection with Search */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Grade <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search and select grade..."
                          value={gradeSearchQuery}
                          onChange={(e) => {
                            setGradeSearchQuery(e.target.value)
                            setShowGradeDropdown(true)
                          }}
                          onFocus={() => {
                            setShowGradeDropdown(true)
                            // Show all grades when focused if no current search
                            if (!gradeSearchQuery && grades.length > 0) {
                              setGradeSearchResults(grades)
                            }
                          }}
                          onBlur={() => {
                            // Delay hiding to allow click events
                            setTimeout(() => setShowGradeDropdown(false), 150)
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {showGradeDropdown && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {gradeSearchLoading ? (
                              <div className="px-4 py-3 text-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
                                <div className="text-sm text-gray-500 mt-2">Searching grades...</div>
                              </div>
                            ) : displayGrades.length > 0 ? (
                              displayGrades.map(grade => (
                                <button
                                  key={grade.id}
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault() // Prevent input blur
                                    handleGradeSelection(grade)
                                  }}
                                  className="w-full px-4 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="font-medium">{grade.name}</div>
                                  {grade.description && (
                                    <div className="text-sm text-gray-500">{grade.description}</div>
                                  )}
                                  <div className="text-xs text-blue-500 mt-1">
                                    {grade.streamCount || 0} stream(s) available
                                  </div>
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-2 text-gray-500 text-sm">
                                {gradeSearchQuery ? 'No grades found. Try a different search.' : 'Start typing to search grades'}
                              </div>
                            )}
                          </div>
                        )}
                        {studentForm.watch('gradeId') && !showGradeDropdown && (
                          <div className="mt-2 flex items-center justify-between">
                            <div className="flex items-center text-sm text-green-600">
                              <CheckCircleIcon className="h-4 w-4 mr-1" />
                              Selected: {grades.find(g => (g.id || g._id) === studentForm.watch('gradeId'))?.name}
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                studentForm.setValue('gradeId', '')
                                studentForm.setValue('streamId', '')
                                setSelectedGrade('')
                                setGradeSearchQuery('')
                                setStreamSearchQuery('')
                                setStreams([])
                              }}
                              className="text-xs text-red-600 hover:text-red-800"
                            >
                              Clear
                            </button>
                          </div>
                        )}
                      </div>
                      {studentForm.formState.errors.gradeId && (
                        <p className="text-red-500 text-sm mt-1">{studentForm.formState.errors.gradeId.message}</p>
                      )}
                    </div>

                    {/* Stream Selection with Search */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Stream <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder={selectedGrade ? "Search and select stream..." : "Select grade first"}
                          value={streamSearchQuery}
                          onChange={(e) => {
                            setStreamSearchQuery(e.target.value)
                            setShowStreamDropdown(true)
                          }}
                          onFocus={() => {
                            if (selectedGrade) {
                              setShowStreamDropdown(true)
                              // Show all streams when focused if no current search
                              if (!streamSearchQuery && streams.length > 0) {
                                setStreamSearchResults(streams)
                              }
                            }
                          }}
                          onBlur={() => {
                            // Delay hiding to allow click events
                            setTimeout(() => setShowStreamDropdown(false), 150)
                          }}
                          disabled={!selectedGrade}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                        {showStreamDropdown && selectedGrade && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {streamSearchLoading ? (
                              <div className="px-4 py-3 text-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
                                <div className="text-sm text-gray-500 mt-2">Searching streams...</div>
                              </div>
                            ) : displayStreams.length > 0 ? (
                              displayStreams.map(stream => (
                                <button
                                  key={stream.id}
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault() // Prevent input blur
                                    handleStreamSelection(stream)
                                  }}
                                  className="w-full px-4 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="font-medium">{stream.name}</div>
                                  {stream.classTeacher && (
                                    <div className="text-sm text-gray-500">Teacher: {stream.classTeacher}</div>
                                  )}
                                  {stream.description && (
                                    <div className="text-xs text-gray-400">{stream.description}</div>
                                  )}
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-2 text-gray-500 text-sm">
                                {streamSearchQuery ? 'No streams found. Try a different search.' : 
                                 streams.length === 0 ? 'No streams available for this grade' : 'Start typing to search streams'}
                              </div>
                            )}
                          </div>
                        )}
                        {studentForm.watch('streamId') && !showStreamDropdown && (
                          <div className="mt-2 flex items-center justify-between">
                            <div className="flex items-center text-sm text-green-600">
                              <CheckCircleIcon className="h-4 w-4 mr-1" />
                              Selected: {streams.find(s => (s.id || s._id) === studentForm.watch('streamId'))?.name}
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                studentForm.setValue('streamId', '')
                                setStreamSearchQuery('')
                              }}
                              className="text-xs text-red-600 hover:text-red-800"
                            >
                              Clear
                            </button>
                          </div>
                        )}
                      </div>
                      {studentForm.formState.errors.streamId && (
                        <p className="text-red-500 text-sm mt-1">{studentForm.formState.errors.streamId.message}</p>
                      )}
                      {selectedGrade && streams.length === 0 && !showStreamDropdown && (
                        <p className="text-yellow-600 text-sm mt-1">No streams available for selected grade</p>
                      )}
                    </div>

                    {/* Visual Progress Indicator */}
                    <div className="col-span-2">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center space-x-4">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            studentForm.watch('gradeId') ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                          }`}>
                            1
                          </div>
                          <div className="flex-1">
                            <span className="text-sm font-medium text-blue-900">
                              Grade: {studentForm.watch('gradeId') ? 
                                (Array.isArray(grades) ? grades.find(g => (g.id || g._id) === studentForm.watch('gradeId'))?.name : 'Selected') || 'Selected' : 
                                'Not selected'
                              }
                            </span>
                          </div>
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            studentForm.watch('streamId') ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                          }`}>
                            2
                          </div>
                          <div className="flex-1">
                            <span className="text-sm font-medium text-blue-900">
                              Stream: {studentForm.watch('streamId') ? 
                                (Array.isArray(streams) ? streams.find(s => (s.id || s._id) === studentForm.watch('streamId'))?.name : 'Selected') || 'Selected' : 
                                'Not selected'
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Input
                      {...studentForm.register('dateOfBirth')}
                      type="date"
                      label="Date of Birth"
                      max={new Date().toISOString().split('T')[0]}
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      error={studentForm.formState.errors.dateOfBirth?.message}
                    />

                    <Input
                      {...studentForm.register('studentId')}
                      label="Student ID (Optional)"
                      placeholder="Leave empty for auto-generation"
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      error={studentForm.formState.errors.studentId?.message}
                    />
                    {!studentForm.watch('studentId') && (
                      <p className="text-sm text-blue-600 mt-1">
                        💡 Student ID will be auto-generated by the system if left empty
                      </p>
                    )}
                  </div>

                  <div className="flex justify-end space-x-3 pt-6 border-t">
                    <Button
                      type="button"
                      onClick={resetWizard}
                      variant="secondary"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Next: Parent Details
                      <ArrowRightIcon className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </form>
              )}

              {/* Step 2: Parent Details */}
              {currentStep === 2 && (
                <form onSubmit={parentForm.handleSubmit(handleFormWizardSubmit)} className="p-6 space-y-4" autoComplete="off">
                  <div className="text-center mb-6">
                    <UserGroupIcon className="mx-auto h-12 w-12 text-purple-600" />
                    <h4 className="mt-4 text-lg font-semibold text-gray-900">Parent Information</h4>
                    <p className="text-sm text-gray-600">
                      Enter parent/guardian details for {studentData?.firstName} {studentData?.lastName}
                    </p>
                  </div>

                  {/* Hidden fields to prevent autofill */}
                  <input type="text" style={{display:'none'}} />
                  <input type="password" style={{display:'none'}} />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      {...parentForm.register('firstName')}
                      label="Parent First Name"
                      placeholder="e.g., Mary"
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      error={parentForm.formState.errors.firstName?.message}
                    />

                    <Input
                      {...parentForm.register('lastName')}
                      label="Parent Last Name"
                      placeholder="e.g., Doe"
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      error={parentForm.formState.errors.lastName?.message}
                    />

                    <Input
                      {...parentForm.register('email')}
                      type="email"
                      label="Parent Email"
                      placeholder="e.g., mary.doe@parent.co.ke"
                      autoComplete="new-email"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      error={parentForm.formState.errors.email?.message}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Parent Phone <span className="text-red-500">*</span>
                      </label>
                      <PhoneNumberInput
                        value={parentForm.watch('phone') || ''}
                        onChange={(phone) => parentForm.setValue('phone', phone)}
                        placeholder="Enter phone number"
                        error={parentForm.formState.errors.phone?.message}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Relationship (Optional)
                      </label>
                      <select
                        {...parentForm.register('relationship')}
                        className="form-input"
                        autoComplete="off"
                        data-lpignore="true"
                        data-1p-ignore="true"
                      >
                        <option value="">Select Relationship</option>
                        <option value="Father">Father</option>
                        <option value="Mother">Mother</option>
                        <option value="Guardian">Guardian</option>
                        <option value="Grandfather">Grandfather</option>
                        <option value="Grandmother">Grandmother</option>
                        <option value="Uncle">Uncle</option>
                        <option value="Aunt">Aunt</option>
                        <option value="Other Relative">Other Relative</option>
                      </select>
                    </div>

                    <Input
                      {...parentForm.register('occupation')}
                      label="Occupation (Optional)"
                      placeholder="e.g., Teacher, Engineer"
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      error={parentForm.formState.errors.occupation?.message}
                    />

                    <Input
                      {...parentForm.register('address')}
                      label="Home Address (Optional)"
                      placeholder="e.g., 123 Main Street, Nairobi"
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      error={parentForm.formState.errors.address?.message}
                    />

                    <Input
                      {...parentForm.register('emergencyContact')}
                      label="Emergency Contact (Optional)"
                      placeholder="e.g., James Doe"
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      error={parentForm.formState.errors.emergencyContact?.message}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Emergency Phone (Optional)
                      </label>
                      <PhoneNumberInput
                        value={parentForm.watch('emergencyPhone') || ''}
                        onChange={(phone) => parentForm.setValue('emergencyPhone', phone)}
                        placeholder="Enter emergency contact phone"
                        error={parentForm.formState.errors.emergencyPhone?.message}
                      />
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <h5 className="text-sm font-medium text-green-900">Ready to Create Family Accounts</h5>
                        <div className="text-sm text-green-700 mt-1 space-y-1">
                          <p>• Student: {studentData?.firstName} {studentData?.lastName} ({studentData?.email})</p>
                          <p>• Grade: {Array.isArray(grades) && studentData?.gradeId ? grades.find(g => (g.id || g._id) === studentData?.gradeId)?.name || 'Selected' : 'Selected'}</p>
                          <p>• Stream: {Array.isArray(streams) && studentData?.streamId ? streams.find(s => (s.id || s._id) === studentData?.streamId)?.name || 'Selected' : 'Selected'}</p>
                          <p>• Both accounts will be created with auto-generated passwords</p>
                          <p>• Welcome emails will be sent to both student and parent</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between pt-6 border-t">
                    <Button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      variant="secondary"
                    >
                      <ArrowLeftIcon className="h-4 w-4 mr-2" />
                      Back to Student
                    </Button>
                    <Button
                      type="submit"
                      disabled={isUploading}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {isUploading ? (
                        <>Creating Accounts...</>
                      ) : (
                        <>
                          <CheckCircleIcon className="h-4 w-4 mr-2" />
                          Create Family Accounts
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default OnboardPage